import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  CustomerAppUser,
  CustomerAppUserService,
} from '@api/services/customer-app-user.service';
import { InputComponent } from '@ui/input/input.component';
import { ModalComponent } from '@ui/modal/modal.component';
import { MODAL_REF, ModalRef, ModalService } from '@ui/modal/modal.service';
import { NotificationService } from '@ui/notification/notification.service';

@Component({
  selector: 'app-customer-app-users-modal',
  standalone: true,
  imports: [CommonModule, ModalComponent, InputComponent, ReactiveFormsModule],
  template: `
    <ui-modal
      title="App-gebruiker aanpassen"
      submitText="Opslaan"
      cancelText="Annuleren"
      (close)="closeModal()"
      (save)="saveModal()"
    >
      <form [formGroup]="form">
        <div class="flex flex-col gap-4">
          <ui-input
            id="name"
            label="Naam"
            placeholder="Naam"
            formControlName="name"
            [required]="true"
          />
          <ui-input
            id="email"
            label="E-mail"
            placeholder="E-mail"
            formControlName="email"
            [required]="true"
            [autocomplete]="'off'"
          />
          @if (user?.profile?.afas_id; as afasId) {
            <p class="text-sm text-slate-500">AFAS-id: {{ afasId }}</p>
          }
        </div>
      </form>
    </ui-modal>
  `,
  styles: `
    :host {
      display: block;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerAppUsersModalComponent implements OnInit {
  private customerAppUserService = inject(CustomerAppUserService);
  private notificationService = inject(NotificationService);
  private modalRef = inject(MODAL_REF);
  private modalService = inject(ModalService);

  user: CustomerAppUser | null = null;
  form: FormGroup = new FormGroup({});

  ngOnInit() {
    this.form = new FormGroup({
      name: new FormControl(this.user?.name, Validators.required),
      email: new FormControl(this.user?.email, [
        Validators.required,
        Validators.email,
      ]),
    });
  }

  closeModal() {
    this.modalService.close(this.modalRef as ModalRef<unknown>);
  }

  saveModal() {
    this.form.markAllAsTouched();
    if (this.form.invalid || !this.user?.id) {
      this.notificationService.error('Er zijn fouten in het formulier');
      return;
    }

    this.customerAppUserService
      .updateUser(this.user.id, {
        name: this.form.value.name,
        email: this.form.value.email,
      })
      .subscribe({
        next: () => {
          this.notificationService.success('App-gebruiker bijgewerkt');
          this.modalService.close(this.modalRef as ModalRef<unknown>);
        },
        error: (error) => {
          const message =
            error?.error?.errors?.email?.[0] ||
            error?.error?.message ||
            'Opslaan mislukt';
          this.notificationService.error(message);
        },
      });
  }
}
